import Phaser from 'phaser';
import type {
  AbilityDTO,
  AbilityDisplayAction,
  AbilityDisplayAoeExplosionAction,
  AbilityDisplayProjectileAction,
  CombatPosition,
} from '@hexcrawl/shared-utils';
import { abilityService } from '@/ui/services/abilityService';
import type { AbilityDisplayEventPayload } from '@/ui/services/abilityDisplayEvents';
import type { CombatCoordinates } from './CombatCoordinates';
import type { CombatContext } from './CombatContext';
import type { CombatEntityManager } from './CombatEntityManager.ts';
import { DepthOrder } from './constants';

const PROJECTILE_SPEED_PX_PER_SEC = 1000;
const PROJECTILE_MIN_DURATION_MS = 150;
const AOE_TILE_STAGGER_MS = 80;

export class CombatAbilityDisplay {
  constructor(
    private readonly scene: Phaser.Scene,
    private readonly coords: CombatCoordinates,
    private readonly entityManager: CombatEntityManager,
    private readonly context: CombatContext,
  ) {}

  // --- Fire-and-forget display (used by event bus) ---

  handleAbilityDisplayEvent(payload: AbilityDisplayEventPayload) {
    if (payload.casterPosition && payload.targetPosition) {
      const casterSprite = this.entityManager.sprites.get(payload.casterId);
      const casterPos = payload.casterPosition;
      const targetPos = payload.targetPosition;
      if (casterSprite && (casterPos.x !== targetPos.x || casterPos.y !== targetPos.y)) {
        casterSprite.setFacing(this.coords.getFacingBetweenPositions(casterPos, targetPos));
      }
    }

    const cached = abilityService.getAbilityFromCache(payload.abilityId);
    if (cached) {
      this.runAbilityDisplayActions(cached, payload);
      return;
    }

    abilityService.getAbility(payload.abilityId)
      .then((ability) => this.runAbilityDisplayActions(ability, payload))
      .catch((err) => {
        console.warn(`[CombatAbilityDisplay] Failed to load ability ${payload.abilityId} for display events`, err);
      });
  }

  private runAbilityDisplayActions(ability: AbilityDTO, payload: AbilityDisplayEventPayload) {
    if (!ability.displayEvents?.length) return;
    for (const displayEvent of ability.displayEvents) {
      if (displayEvent.trigger !== payload.trigger) continue;
      for (const action of displayEvent.actions) {
        this.executeAbilityDisplayAction(action, payload);
      }
    }
  }

  private executeAbilityDisplayAction(action: AbilityDisplayAction, payload: AbilityDisplayEventPayload) {
    switch (action.type) {
      case 'spriteAnimation':
        this.playDisplayAnimation(action.entityId, action.animation, payload);
        break;
      case 'particleEffect':
        this.spawnDisplayParticle(action, payload);
        break;
      case 'audioClip':
        this.playDisplayAudio(action.clip);
        break;
      case 'projectile':
        this.spawnProjectile(action, payload);
        break;
      case 'aoeExplosion':
        this.spawnAoeExplosion(action, payload);
        break;
      default:
        break;
    }
  }

  private playDisplayAnimation(entityId: string, animation: string, payload: AbilityDisplayEventPayload) {
    const resolvedId = this.resolveEntityId(entityId, payload);
    if (!resolvedId) return;
    const sprite = this.entityManager.sprites.get(resolvedId);
    if (!sprite) {
      console.warn(`[CombatAbilityDisplay] Missing sprite for display animation on ${resolvedId}`);
      return;
    }
    sprite.playAnimation(animation);
  }

  private spawnDisplayParticle(action: AbilityDisplayAction, payload: AbilityDisplayEventPayload) {
    if (action.type !== 'particleEffect') return;
    const worldPos = this.resolveParticlePosition(action, payload);
    if (!worldPos) {
      console.warn('[CombatAbilityDisplay] Missing position for particle effect display event');
      return;
    }
    void this.spawnParticleEmitter(action.effect, worldPos.x, worldPos.y);
  }

  private spawnAoeExplosion(action: AbilityDisplayAoeExplosionAction, payload: AbilityDisplayEventPayload) {
    void this.spawnAoeExplosionAsync(action, payload);
  }

  // --- Async display actions (used by turn queue processor) ---

  executeAbilityDisplayActionAsync(
    action: AbilityDisplayAction,
    payload: AbilityDisplayEventPayload
  ): Promise<void> | null {
    switch (action.type) {
      case 'spriteAnimation':
        return this.playDisplayAnimationAsync(action.entityId, action.animation, payload);
      case 'particleEffect':
        return this.spawnDisplayParticleAsync(action, payload);
      case 'audioClip':
        this.playDisplayAudio(action.clip);
        return null;
      case 'projectile':
        return this.spawnProjectileAsync(action, payload);
      case 'aoeExplosion':
        return this.spawnAoeExplosionAsync(action, payload);
      default:
        return null;
    }
  }

  private playDisplayAnimationAsync(
    entityId: string,
    animation: string,
    payload: AbilityDisplayEventPayload
  ): Promise<void> | null {
    const resolvedId = this.resolveEntityId(entityId, payload);
    if (!resolvedId) return null;
    const sprite = this.entityManager.sprites.get(resolvedId);
    if (!sprite) return null;
    return sprite.playAnimation(animation);
  }

  private spawnDisplayParticleAsync(
    action: AbilityDisplayAction,
    payload: AbilityDisplayEventPayload
  ): Promise<void> | null {
    if (action.type !== 'particleEffect') return null;
    const worldPos = this.resolveParticlePosition(action, payload);
    if (!worldPos) return null;
    return this.spawnParticleEmitter(action.effect, worldPos.x, worldPos.y);
  }

  private async spawnAoeExplosionAsync(
    action: AbilityDisplayAoeExplosionAction,
    payload: AbilityDisplayEventPayload
  ): Promise<void> {
    const centerPos = this.resolveEntityPosition(action.center, payload);
    if (!centerPos) return;

    const ability = abilityService.getAbilityFromCache(payload.abilityId);
    const aoeRadius = ability?.target.aoeRadius ?? 2;

    const tiles = this.getAoeRadiusTiles(centerPos, aoeRadius);

    await Promise.all(tiles.map((tile) => {
      const dist = Math.abs(tile.x - centerPos.x) + Math.abs(tile.y - centerPos.y);
      const delay = dist * AOE_TILE_STAGGER_MS;
      const worldPos = this.coords.tileToWorld(tile.x + 0.5, tile.y + 0.5);
      return this.spawnParticleEmitter(action.effect, worldPos.x, worldPos.y - 8, delay);
    }));
  }

  private spawnProjectile(action: AbilityDisplayProjectileAction, payload: AbilityDisplayEventPayload) {
    const fromWorld = this.resolveEntityWorldMidpoint(action.from, payload);
    const toWorld = action.toPosition
      ? (() => { const w = this.coords.tileToWorld(action.toPosition!.x + 0.5, action.toPosition!.y + 0.5); return { x: w.x, y: w.y - 16 }; })()
      : this.resolveEntityWorldMidpoint(action.to, payload);
    if (!fromWorld || !toWorld) return;

    const frameKey = action.sprite;
    if (!this.scene.textures.get('projectiles')?.has(frameKey)) {
      console.warn(`[CombatAbilityDisplay] Projectile frame '${frameKey}' not found in atlas`);
      return;
    }

    const dx = toWorld.x - fromWorld.x;
    const dy = toWorld.y - fromWorld.y;
    const duration = Math.max(PROJECTILE_MIN_DURATION_MS, (Math.sqrt(dx * dx + dy * dy) / PROJECTILE_SPEED_PX_PER_SEC) * 1000);

    const projectile = this.scene.add.image(fromWorld.x, fromWorld.y, 'projectiles', frameKey);
    projectile.setRotation(-Math.atan2(dx, dy));
    projectile.setScale(0.5);
    projectile.setDepth(DepthOrder.PARTICLES);

    this.scene.tweens.add({
      targets: projectile,
      x: toWorld.x,
      y: toWorld.y,
      duration,
      ease: 'Linear',
      onComplete: () => projectile.destroy(),
    });
  }

  private async spawnProjectileAsync(
    action: AbilityDisplayProjectileAction,
    payload: AbilityDisplayEventPayload
  ): Promise<void> {
    const frameKey = action.sprite;
    if (!this.scene.textures.get('projectiles')?.has(frameKey)) {
      console.warn(`[CombatAbilityDisplay] Projectile frame '${frameKey}' not found in atlas`);
      return;
    }

    const fromWorld = this.resolveEntityWorldMidpoint(action.from, payload);
    const toWorld = action.toPosition
      ? (() => { const w = this.coords.tileToWorld(action.toPosition!.x + 0.5, action.toPosition!.y + 0.5); return { x: w.x, y: w.y - 16 }; })()
      : this.resolveEntityWorldMidpoint(action.to, payload);
    if (!fromWorld || !toWorld) return;

    const dx = toWorld.x - fromWorld.x;
    const dy = toWorld.y - fromWorld.y;
    const duration = Math.max(PROJECTILE_MIN_DURATION_MS, (Math.sqrt(dx * dx + dy * dy) / PROJECTILE_SPEED_PX_PER_SEC) * 1000);

    const projectile = this.scene.add.image(fromWorld.x, fromWorld.y, 'projectiles', frameKey);
    projectile.setRotation(-Math.atan2(dx, dy));
    projectile.setScale(0.5);
    projectile.setDepth(DepthOrder.PARTICLES);

    return new Promise<void>((resolve) => {
      this.scene.tweens.add({
        targets: projectile,
        x: toWorld.x,
        y: toWorld.y,
        duration,
        ease: 'Linear',
        onComplete: () => {
          projectile.destroy();
          resolve();
        },
      });
    });
  }

  // --- Shared helpers ---

  private resolveEntityWorldMidpoint(
    entityId: string | undefined,
    payload: AbilityDisplayEventPayload,
  ): { x: number; y: number } | undefined {
    const resolvedId = this.resolveEntityId(entityId, payload);
    if (resolvedId) {
      const sprite = this.entityManager.sprites.get(resolvedId);
      if (sprite) {
        return { x: sprite.x, y: sprite.y - sprite.sprite.displayHeight / 2 };
      }
    }
    // Fall through to position-based resolution (handles AoE where targetId is undefined)
    const pos = this.resolveEntityPosition(entityId, payload);
    if (!pos) return undefined;
    const world = this.coords.tileToWorld(pos.x + 0.5, pos.y + 0.5);
    return { x: world.x, y: world.y - 16 };
  }

  private resolveParticlePosition(
    action: AbilityDisplayAction,
    payload: AbilityDisplayEventPayload,
  ): { x: number; y: number } | undefined {
    if (action.type !== 'particleEffect') return undefined;

    if (action.position) {
      const w = this.coords.tileToWorld(action.position.x + 0.5, action.position.y + 0.5);
      return { x: w.x, y: w.y - 8 };
    }

    return this.resolveEntityWorldMidpoint(action.entityId, payload);
  }

  resolveEntityId(entityId: string | undefined, payload: AbilityDisplayEventPayload): string | undefined {
    if (!entityId) return undefined;
    if (entityId === 'caster') return payload.casterId;
    if (entityId === 'target') return payload.targetId;
    return entityId;
  }

  resolveEntityPosition(entityId: string | undefined, payload: AbilityDisplayEventPayload): CombatPosition | undefined {
    if (!entityId) return undefined;
    if (entityId === 'caster') return payload.casterPosition;
    if (entityId === 'target') return payload.targetPosition;
    const entityState = this.context.combat.entityStates.find((state) => state.entityId === entityId);
    return entityState?.position;
  }

  private getAoeRadiusTiles(center: CombatPosition, radius: number): CombatPosition[] {
    const tiles: CombatPosition[] = [];
    for (let dx = -radius; dx <= radius; dx++) {
      for (let dy = -radius; dy <= radius; dy++) {
        if (Math.abs(dx) + Math.abs(dy) <= radius) {
          tiles.push({ x: center.x + dx, y: center.y + dy });
        }
      }
    }
    return tiles;
  }

  private playDisplayAudio(clip: string) {
    if (!this.scene.cache.audio.exists(clip)) {
      console.warn(`[CombatAbilityDisplay] Missing audio clip '${clip}' for display event`);
      return;
    }
    this.scene.sound.play(clip);
  }

  private buildPhaserEmitterConfig(def: Record<string, unknown>): Phaser.Types.GameObjects.Particles.ParticleEmitterConfig {
    const args = (def.particleArguments ?? {}) as Record<string, any>;
    const config: Phaser.Types.GameObjects.Particles.ParticleEmitterConfig = {};

    if (args.lifespan) config.lifespan = args.lifespan;
    if (def.gravityY !== undefined) config.gravityY = def.gravityY as number;
    if (def.gravityX !== undefined) config.gravityX = def.gravityX as number;

    if (args.color?.start && args.color?.end) {
      config.color = [
        Phaser.Display.Color.GetColor(args.color.start.r, args.color.start.g, args.color.start.b),
        Phaser.Display.Color.GetColor(args.color.end.r, args.color.end.g, args.color.end.b),
      ];
    }

    if (def.scaleFromX !== undefined) {
      config.scale = { start: def.scaleFromX as number, end: (def.scaleToX as number) ?? 0 };
    }

    config.alpha = { start: (def.alphaMax as number) ?? 1, end: (def.alphaMin as number) ?? 0 };

    if (args.startRotation) {
      config.rotate = { min: args.startRotation.min, max: args.startRotation.max };
    }

    if (def.minSpeedX !== undefined || def.maxSpeedX !== undefined) {
      config.speedX = { min: Number(def.minSpeedX ?? 0), max: Number(def.maxSpeedX ?? 0) };
      config.speedY = { min: Number(def.minSpeedY ?? 0), max: Number(def.maxSpeedY ?? 0) };
    }

    if (def.blendMode !== undefined) config.blendMode = def.blendMode as number;

    const w = Number(def.width) || 0;
    const h = Number(def.height) || 0;
    if (w > 0 && h > 0) {
      config.emitZone = {
        type: 'random',
        source: new Phaser.Geom.Rectangle(-w / 2, -h / 2, w, h),
      } as Phaser.Types.GameObjects.Particles.ParticleEmitterRandomZoneConfig;
    }

    const total = Number(def.total) || 10;
    config.quantity = total;
    config.stopAfter = total;

    return config;
  }

  private spawnParticleEmitter(effect: string, x: number, y: number, delayMs = 0): Promise<void> {
    const data = this.scene.cache.json.get('particles') as { emitters: Record<string, Record<string, unknown>> } | null;
    const def = data?.emitters?.[effect];
    if (!def) {
      console.warn(`[CombatAbilityDisplay] Unknown particle effect '${effect}'`);
      return Promise.resolve();
    }

    const config = this.buildPhaserEmitterConfig(def);
    const args = (def.particleArguments ?? {}) as Record<string, any>;
    const lifespan = args.lifespan ?? 1000;
    const maxLifespan: number = typeof lifespan === 'object' ? lifespan.max : lifespan;

    return new Promise<void>((resolve) => {
      const create = () => {
        const emitter = this.scene.add.particles(x, y, effect, config);
        emitter.setDepth(DepthOrder.PARTICLES);
        this.scene.time.delayedCall(maxLifespan, () => {
          emitter.destroy();
          resolve();
        });
      };

      if (delayMs > 0) {
        this.scene.time.delayedCall(delayMs, create);
      } else {
        create();
      }
    });
  }
}
